import { Col, Descriptions, Flex, Row, Text } from "@/atoms"
import { NA_VALUE } from "@/const/default-values"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { ScanStatusTag } from "@/molecules/ScanStatusTag"
import { useApplicationEntity } from "@/organisms/Drawers/applications/inventory/useInventory"
import { OverviewCard } from "@/organisms/Drawers/components/OverviewCard"
import { ResourceOverviewCard } from "@/organisms/Drawers/components/ResourceOverviewCard"
import { getAuthenticationItems } from "@/pages/DiscoveryPage/application/application-inventory/useApplicationInventory.selector"
import { InsightsApplicationCell } from "@/pages/DiscoveryPage/components/molecules/InsightsApplicationCell"

import type { DescriptionItem } from "@/atoms"
import type { TabComponentProps } from "@/organisms/Drawers/components/TabsDrawerTemplate"

export const OverviewTabApplicationDrawer = (props: TabComponentProps) => {
  const { t } = useTranslation()
  const { t: tOverview } = useTranslation("translation", {
    keyPrefix: "inventory.application.drawer.overview"
  })
  const context = useApplicationEntity(props.record?.id)
  const [{ response, isLoading }] = context || {}
  const { data } = response || {}
  const internetFacingFF = useFeatureFlags("internetFacingIntegration")
  const applicationRecord = data?.[0]

  if (isLoading || !applicationRecord) return null

  const {
    labels,
    annotations,
    securityContext,
    operatingSystems,
    owner,
    tags,
    authentication,
    executionRoleArn,
    workloadIdentityArn
  } = applicationRecord
  const relatedImages = applicationRecord.relatedImages?.items ?? []

  // A key/value map (Kubernetes labels/annotations) as a titled section of description rows.
  // The API omits the map when there is nothing to show, which is always the case for
  // workloads without Kubernetes metadata (ECS, VM containers, Lambda, Azure apps), so the
  // section is left out instead of showing an empty state.
  const mapSection = (
    map: Record<string, string> | undefined,
    titleKey: string
  ): DescriptionItem[] => {
    const entries = Object.entries(map ?? {})

    if (entries.length === 0) return []

    return [
      { label: <Descriptions.Divider />, children: null, span: 2 },
      { label: tOverview(titleKey), children: null, span: 2 },
      // Values (esp. annotations like last-applied-configuration) can be multi-KB
      // blobs, so truncate with a tooltip instead of dumping them verbatim and
      // wrecking the drawer layout.
      ...entries.map(([key, value]) => ({
        label: (
          <Descriptions.Label textTransform="unset">{key}</Descriptions.Label>
        ),
        children: <Text className="sm-text-ks">{value}</Text>,
        span: -1
      }))
    ]
  }

  // Agent applications (AgentCore) only: a general row per value the API sends, none otherwise.
  const optionalGeneralRow = (labelKey: string, value?: string | null) =>
    value
      ? [
          {
            label: (
              <Descriptions.Label>
                {tOverview(`details.general.${labelKey}`)}
              </Descriptions.Label>
            ),
            children: <Text className="sm-text-ks">{value}</Text>
          }
        ]
      : []

  // Only agent applications (AgentCore) report how callers authenticate; the section is left out
  // for every other application.
  const authenticationItems = getAuthenticationItems(authentication)
  const authenticationSection: DescriptionItem[] = authenticationItems.length
    ? [
        { label: <Descriptions.Divider />, children: null, span: 2 },
        {
          label: tOverview("details.authentication.title"),
          children: null,
          span: 2
        },
        ...authenticationItems.map(({ key, value }) => ({
          label: (
            <Descriptions.Label>
              {tOverview(`details.authentication.${key}`)}
            </Descriptions.Label>
          ),
          children: (
            <Text className="sm-text-ks">
              {key === "type"
                ? tOverview(`details.authentication.types.${value}`)
                : value}
            </Text>
          )
        }))
      ]
    : []

  // Aggregated privilege flags are tri-state: true -> Enabled, false -> Disabled,
  // null/undefined -> Not available (the sensor did not report it).
  const privilegeStatus = (value?: boolean | null) =>
    value == null
      ? tOverview("privileges.notAvailable")
      : tOverview(value ? "privileges.enabled" : "privileges.disabled")

  const privilegeItems: DescriptionItem[] = [
    { label: tOverview("privileges.mode"), value: securityContext?.privileged },
    {
      label: tOverview("privileges.readOnly"),
      value: securityContext?.readOnlyRootFilesystem
    },
    { label: tOverview("privileges.root"), value: securityContext?.runAsRoot },
    {
      label: tOverview("privileges.privilege"),
      value: securityContext?.allowPrivilegeEscalation
    }
  ].map(({ label, value }) => ({
    // NOTE: no span:2 here — the Descriptions grid treats span===2 as a
    // full-width, label-only row and drops the content cell, which hid every
    // privilege value. These are label+value rows, so use the default span.
    label: (
      <Descriptions.Label textTransform="unset">{label}</Descriptions.Label>
    ),
    children: (
      <Text type="secondary" className="sm-text-ks">
        {privilegeStatus(value)}
      </Text>
    )
  }))

  return (
    <Row gutter={16} className="body-tab-drawer-ks">
      <Col span={14}>
        <OverviewCard
          title={tOverview("details.title")}
          items={[
            {
              label: tOverview("details.general.title"),
              children: null,
              span: 2
            },
            {
              label: (
                <Descriptions.Label>{t("general.insights")}</Descriptions.Label>
              ),
              children: InsightsApplicationCell({
                t,
                internetFacingFF,
                tag: true
              })(
                // applicationRecord.insights is already booleanized by
                // getApplicationTabData; do NOT transform again (double-applying
                // turns internetFacing into a boolean and drops publicImage).
                applicationRecord.insights,
                applicationRecord
              )
            },
            {
              label: (
                <Descriptions.Label>
                  {tOverview("details.general.os")}
                </Descriptions.Label>
              ),
              children: operatingSystems?.[0]?.name
                ? capitalizeFirstLetter(operatingSystems[0].name)
                : NA_VALUE
            },
            {
              label: (
                <Descriptions.Label>
                  {tOverview("details.general.workloadType")}
                </Descriptions.Label>
              ),
              children: t(
                `inventory.application.workloadKind.${applicationRecord?.workloadKind}`,
                "-"
              )
            },
            {
              label: (
                <Descriptions.Label>
                  {tOverview("details.general.creationDate")}
                </Descriptions.Label>
              ),
              children: applicationRecord.creationDateUserLocale
            },
            {
              label: (
                <Descriptions.Label>
                  {tOverview("details.general.scanningStatus")}
                </Descriptions.Label>
              ),
              children: <ScanStatusTag status="Done" />
            },
            {
              label: (
                <Descriptions.Label>{t("general.lastSeen")}</Descriptions.Label>
              ),
              children: applicationRecord.lastSeenUserLocale
            },
            ...(owner
              ? [
                  {
                    label: (
                      <Descriptions.Label>
                        {tOverview("details.general.owner")}
                      </Descriptions.Label>
                    ),
                    children: owner
                  }
                ]
              : []),
            ...optionalGeneralRow("executionRole", executionRoleArn),
            ...optionalGeneralRow("workloadIdentity", workloadIdentityArn),
            ...authenticationSection,
            ...mapSection(tags ?? undefined, "details.tags.title"),
            ...mapSection(labels, "details.labels.title"),
            ...mapSection(annotations, "details.annotations.title")
          ]}
        />
      </Col>
      <Col span={10}>
        <Flex vertical gap={16}>
          {/* Lambda functions run no image, so the API sends no related images */}
          {relatedImages.length > 0 && (
            <ResourceOverviewCard
              title={tOverview("relatedImage.title")}
              type="ImageRepository"
              imageSource={relatedImages[0].imageSource}
              images={relatedImages}
              paramsExternalLink={
                applicationRecord.queryParamsRelatedImagesExternalLink
              }
            />
          )}
          {/* The API sends no security context for workloads without Kubernetes runtime context */}
          {securityContext && (
            <OverviewCard
              title={tOverview("privileges.title")}
              items={privilegeItems}
            />
          )}
        </Flex>
      </Col>
    </Row>
  )
}
